import React, { memo, useEffect, useState ,useContext} from "react";
import { useFormContext, Controller } from "react-hook-form";
import { MDBRow, MDBCol } from "mdbreact";
import { Label } from "abs-ui-components";
import AutocompleteDropdown from "../../../common/autocompleteDropdown/AutocompleteDropdown";
import axiosHttp from "../../../services/interceptor";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { RiskAssessment } from "../../../components/RiskAssessment/RiskAssessment";
import { ClaimsContext } from "../../../services/ClaimsService";
import { ClaimType } from "../../../constants/claims";

const baseURL = process.env.REACT_APP_BASE_URL;

const ManagementSubmitterFormGroup = (props) => {
    const { control, formState: { errors } } = useFormContext();  
    const { userHasClaim } = useContext(ClaimsContext);	
	const [hasRiskAssessmentAccess, setRiskAssessment] = useState(false);
	const [vesselData, setVesselData] = useState(() => {
		if (props?.isEnrollVessel && !props.vesselData) {
			return {
				InherentRiskAssessmentMst: { RiskAssessmentMstId: null },
				ControlRiskAssessmentMst: { RiskAssessmentMstId: null },
			};
		}
		return props.vesselData || {};
	});

	const wcnLoadOptions = async (inputValue) => {
		const requestedPayload = { searchKey: inputValue };
		try {
			const response = await axiosHttp.post(`${baseURL}api/Wcn/SearchWcn`, requestedPayload);
			const formattedWCNOptions = response?.data?.map((item) => ({
				label: `${item.CompanyName}`,
				value: `${item.WcnNumber}`,
				wcnMappingId: item.WcnMstId,
				controlRiskId: item.ControlRiskId,
				inherentRiskId: item.InherentRiskId,
			}));
			return formattedWCNOptions;
		} catch (error) {
			emitToasterMsg("error", "Error", error.message);
			return [];
		}
	};

	useEffect(() => {		
		const isValid = userHasClaim(ClaimType.VIEW_RISK_ASSESSMENT);
        setRiskAssessment(isValid);
	}, [vesselData]);

	const debounceAsync = (func, wait) => {
		let timeout;
		return (...args) => {
			return new Promise((resolve) => {
				if (timeout) clearTimeout(timeout);
				timeout = setTimeout(async () => {
					const result = await func(...args);
					resolve(result);
				}, wait);
			});
		};
	};

	const fetchWcnOptions = async (inputValue) => {
		if (inputValue?.length === 6) {
			try {
				const response = await axiosHttp.get(`${baseURL}api/Wcn/GetByWcnNumber?wcnNumber=${inputValue}`);
				if (response?.data?.isSuccess) {
					return [
						{
							label: response?.data?.data?.companyName,
							value: response?.data?.data?.wcnNumber,
							wcnMappingId: response?.data?.data?.wcnMstId,
							controlRiskId: response?.data?.data?.controlRiskId,
							inherentRiskId: response?.data?.data?.inherentRiskId,
						},
					];
				} else {
					return [];
				}
			} catch (error) {
				emitToasterMsg("error", "Error", error.message);
				return [];
			}
		} else {
			return [];
		}
	};

	const alternateWcnLoadOptions = debounceAsync(fetchWcnOptions, 400);

	const handleWcnChange = (selectedOption) => {
		if (selectedOption) {
			setVesselData({
				InherentRiskAssessmentMst: {
					RiskAssessmentMstId: selectedOption.inherentRiskId,
				},
				ControlRiskAssessmentMst: {
					RiskAssessmentMstId: selectedOption.controlRiskId,
				},
			});
		}
	};

	return (
		<div className="container-fluid p-0 m-0">
			<MDBRow>
				<MDBCol md="12" className="mt-3">
					<Label required bold name="Submitter WCN" className="mb-1" />
					<Controller
						name="vesselIdentifications.managementCompanyWCN"
						control={control}
						rules={
							props?.regulations?.trim() !== "EUMRV" && props?.regulations?.trim() !== "UKMRV" && props?.regulations?.trim() !== "DCS"
								? { required: "Submitter WCN is required" }
								: {}
						}
						defaultValue={
							props.vesselData?.VesselMstId && props?.vesselData?.SubmitterWcnMst
								? {
										label: props?.vesselData?.SubmitterWcnMst?.CompanyName,
										value: props?.vesselData?.SubmitterWcnMst?.WcnNumber,
										wcnMappingId: props?.vesselData?.SubmitterWcnMst?.WcnMstId,
								  }
								: null
						}
						render={({ field }) => (
							<AutocompleteDropdown
								id="management_company_wcn"
								{...field}
								disabled={
									(!props.isEnrollVessel || props.vesselMstId) &&
									(props?.disabled || props?.regulations?.trim() === "EUMRV" || "UKMRV" || "DCS")
								}
								isClearable
								loadOptions={wcnLoadOptions}
								placeholder="Search Submitter WCN"
								getOptionLabel={(option) => `${option.value} - ${option.label}`}
								onChange={(selectedOption) => {
									field.onChange(selectedOption);
									handleWcnChange(selectedOption);
								}}
								defaultOptions={
									props.vesselData?.vesselMstId
										? [
												{
													label: props?.vesselData?.SubmitterWcnMst?.CompanyName,
													value: props?.vesselData?.SubmitterWcnMst?.WcnNumber,
													wcnMappingId: props?.vesselData?.SubmitterWcnMst?.WcnMstId,
												},
										  ]
										: null
								}
							/>
						)}
					/>
					{errors.vesselIdentifications?.managementCompanyWCN && (
						<div className="error-msg">{errors.vesselIdentifications.managementCompanyWCN.message}</div>
					)}
				</MDBCol>
				<MDBCol md="12" className="mt-3">
					<Label bold name="Alternate WCN" className="mb-1" />
					<Controller
						name="vesselIdentifications.submitterWCN"
						control={control}
						defaultValue={
							props.vesselData?.AlternativeWcnMst
								? {
										label: props?.vesselData?.AlternativeWcnMst?.CompanyName,
										value: props?.vesselData?.AlternativeWcnMst?.WcnNumber,
										wcnMappingId: props?.vesselData?.AlternativeWcnMst?.WcnMstId,
								  }
								: null
						}
						render={({ field }) => (
							<AutocompleteDropdown
								id="submitter_wcn"
								{...field}
								disabled={!props.isEnrollVessel && props.vesselMstId && (props?.regulations?.trim() === "EUMRV" || "UKMRV" || "DCS")}
								isClearable
								loadOptions={userHasClaim(ClaimType.VIEW_ABS_WCN) ? wcnLoadOptions : alternateWcnLoadOptions}
								placeholder="Search Alternate WCN"
								getOptionLabel={(option) => `${option.value} - ${option.label}`}
								defaultOptions={
									props.vesselData?.AlternativeWcnMst
										? [
												{
													label: props?.vesselData?.AlternativeWcnMst?.CompanyName,
													value: props?.vesselData?.AlternativeWcnMst?.WcnNumber,
													wcnMappingId: props?.vesselData?.AlternativeWcnMst?.WcnMstId,
												},
										  ]
										: null
								}
							/>
						)}
					/>
					{errors.vesselIdentifications?.submitterWCN && <div className="error-msg">{errors.vesselIdentifications.submitterWCN.message}</div>}
				</MDBCol>
			</MDBRow>
			<MDBRow>
				<MDBCol md="12" className="mt-3">
					<Controller
						name="vesselData.InherentRiskAssessmentMst.RiskAssessmentMstId"
						control={control}
						defaultValue={vesselData?.InherentRiskAssessmentMst?.RiskAssessmentMstId || null}
						render={({ field }) => <input type="hidden" {...field} />}
					/>
					<Controller
						name="vesselData.ControlRiskAssessmentMst.RiskAssessmentMstId"
						control={control}
						defaultValue={vesselData?.ControlRiskAssessmentMst?.RiskAssessmentMstId || null}
						render={({ field }) => <input type="hidden" {...field} />}
					/>

					{hasRiskAssessmentAccess && (
						<RiskAssessment
							inherentRiskValue={vesselData?.InherentRiskAssessmentMst?.RiskAssessmentMstId}
							controlRiskValue={vesselData?.ControlRiskAssessmentMst?.RiskAssessmentMstId}
							disabled={props.disabled || (props.disableRiskAssessment ?? false)}
						/>
					)}
				</MDBCol>
			</MDBRow>
		</div>
	);
};

export default memo(ManagementSubmitterFormGroup);
